import React, { useMemo, useState } from 'react'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import { Box, BoxProps, Button, Flex, Select, Text } from '@workwhile/ui'
import {
  ColumnDef,
  ExpandedState,
  OnChangeFn,
  PaginationState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { TableEmptyState } from './TableEmptyState'

const SHOW_ROWS_OPTIONS = [
  { label: 'Show 10', value: 10 },
  { label: 'Show 25', value: 25 },
  { label: 'Show 50', value: 50 },
  { label: 'Show 100', value: 100 },
]

const INTERACTIVE_ROW_CLICK_SELECTOR =
  'a[href], button, input, select, textarea, [role="button"], [role="menuitem"], [role="checkbox"], label'

const isInteractiveRowClick = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return false
  }
  return target.closest(INTERACTIVE_ROW_CLICK_SELECTOR) !== null
}

type ExpandablePaginatedTableProps<T extends object> = {
  columns: ColumnDef<T>[]
  items: T[]
  rowCount: number
  pagination: PaginationState
  setPagination: OnChangeFn<PaginationState>
  isFetching: boolean
  emptyMessage: React.ReactNode
  emptyStateHeight?: number
  emptyStateMaxWidth?: BoxProps['maxWidth']
  getRowCanExpand: (row: T) => boolean
  renderExpandedContent: (row: T) => React.ReactNode
  showExpandColumn?: boolean
  expandOnRowClick?: boolean
  getRowOpacity?: (row: T) => number
}

export function ExpandablePaginatedTable<T extends object>({
  columns,
  items,
  rowCount,
  pagination,
  setPagination,
  isFetching,
  emptyMessage,
  emptyStateHeight = 258,
  emptyStateMaxWidth,
  getRowCanExpand,
  renderExpandedContent,
  showExpandColumn = true,
  expandOnRowClick = false,
  getRowOpacity,
}: ExpandablePaginatedTableProps<T>) {
  const [expanded, setExpanded] = useState<ExpandedState>({})

  const tableColumns = useMemo<ColumnDef<T>[]>(() => {
    if (!showExpandColumn) {
      return columns
    }

    return [
      {
        id: 'expand',
        header: '',
        size: 48,
        cell: ({ row }) =>
          getRowCanExpand(row.original) ? (
            <Flex
              alignItems="center"
              justifyContent="center"
              color="lightText"
              role="button"
              tabIndex={0}
              aria-expanded={row.getIsExpanded()}
              aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
              onClick={() => row.toggleExpanded()}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  row.toggleExpanded()
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              {row.getIsExpanded() ? (
                <ChevronDown size={18} />
              ) : (
                <ChevronRight size={18} />
              )}
            </Flex>
          ) : null,
      },
      ...columns,
    ]
  }, [columns, getRowCanExpand, showExpandColumn])

  const table = useReactTable({
    columns: tableColumns,
    data: items,
    state: {
      expanded,
      pagination,
    },
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => getRowCanExpand(row.original),
    manualPagination: true,
    rowCount,
    defaultColumn: {
      size: Number.MAX_SAFE_INTEGER,
    },
  })

  const doesHaveData = items.length > 0

  if (!doesHaveData) {
    return (
      <TableEmptyState
        isFetching={isFetching}
        emptyMessage={emptyMessage}
        emptyStateHeight={emptyStateHeight}
        emptyStateMaxWidth={emptyStateMaxWidth}
      />
    )
  }

  const canGetPreviousPage = table.getCanPreviousPage()
  const canGetNextPage = table.getCanNextPage()

  return (
    <Box>
      <Box
        as="table"
        width="100%"
        border="1px solid"
        borderColor="neutrals.100"
        borderRadius="standard"
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <Box as="thead">
          {table.getHeaderGroups().map((headerGroup) => (
            <Box as="tr" key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Box
                  as="th"
                  key={header.id}
                  colSpan={header.colSpan}
                  p={3}
                  fontWeight={500}
                  textAlign="left"
                  color="lightText"
                  backgroundColor="neutrals.100"
                  borderBottom="1px solid"
                  borderColor="neutrals.100"
                  width={
                    header.getSize() === Number.MAX_SAFE_INTEGER
                      ? 'auto'
                      : header.getSize()
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
        <Box as="tbody">
          {table.getRowModel().rows.map((row) => {
            const isExpandable = getRowCanExpand(row.original)
            const isExpanded = row.getIsExpanded()
            const rowOpacity = getRowOpacity?.(row.original)

            return (
              <React.Fragment key={row.id}>
                <Box
                  as="tr"
                  opacity={rowOpacity}
                  data-opacity={rowOpacity}
                  onClick={
                    expandOnRowClick && isExpandable
                      ? (event) => {
                          if (isInteractiveRowClick(event.target)) {
                            return
                          }
                          row.toggleExpanded()
                        }
                      : undefined
                  }
                  style={
                    expandOnRowClick && isExpandable
                      ? { cursor: 'pointer' }
                      : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <Box
                      as="td"
                      key={cell.id}
                      p={3}
                      verticalAlign="top"
                      backgroundColor={isExpanded ? 'neutrals.50' : undefined}
                      width={
                        cell.column.getSize() === Number.MAX_SAFE_INTEGER
                          ? 'auto'
                          : cell.column.getSize()
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Box>
                  ))}
                </Box>
                {isExpanded && isExpandable ? (
                  <Box as="tr">
                    <Box
                      as="td"
                      colSpan={row.getVisibleCells().length}
                      p={3}
                      backgroundColor="neutrals.50"
                      borderBottom="1px solid"
                      borderColor="neutrals.100"
                    >
                      {renderExpandedContent(row.original)}
                    </Box>
                  </Box>
                ) : null}
              </React.Fragment>
            )
          })}
        </Box>
      </Box>

      <Flex alignItems="center" gap={3} mt={2} justifyContent="space-between">
        <Text color="lightText">
          Showing {table.getRowModel().rows.length.toLocaleString()} of{' '}
          {table.getRowCount().toLocaleString()} rows
        </Text>
        <Flex alignItems="center" gap={2}>
          {canGetPreviousPage ? (
            <>
              <Button
                variant="secondary"
                onClick={() => table.firstPage()}
                title="Go to first page"
                disabled={!canGetPreviousPage}
              >
                <ChevronsLeft size={18} />
              </Button>
              <Button
                variant="secondary"
                onClick={() => table.previousPage()}
                title="Go to previous page"
                disabled={!canGetPreviousPage}
              >
                <ChevronLeft size={18} />
              </Button>
            </>
          ) : null}
          <Text color="lightText">
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount().toLocaleString()}
          </Text>
          {canGetNextPage ? (
            <>
              <Button
                variant="secondary"
                onClick={() => table.nextPage()}
                title="Go to next page"
              >
                <ChevronRight size={18} />
              </Button>
              <Button
                variant="secondary"
                onClick={() => table.lastPage()}
                title="Go to last page"
              >
                <ChevronsRight size={18} />
              </Button>
            </>
          ) : null}
          <Select
            isSearchable={false}
            value={SHOW_ROWS_OPTIONS.find(
              (option) => option.value === table.getState().pagination.pageSize
            )}
            onChange={(option) => {
              table.setPageSize(option.value)
            }}
            options={SHOW_ROWS_OPTIONS}
          />
        </Flex>
      </Flex>
    </Box>
  )
}
